export const INTERNAL_APP_ORIGIN = "https://www.lehti.ai";

/** Serialize a same-app URL without leaking its synthetic validation origin. */
export function canonicalInternalPath(url: URL): string {
  if (url.origin !== INTERNAL_APP_ORIGIN) {
    throw new Error(
      "Internal navigation URL must stay on the application origin.",
    );
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

function safePath(value: string | null): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed && trimmed.length <= 2048 ? trimmed : null;
}

export function containsControlCharacter(value: string): boolean {
  return [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
}

function isAllowedReturnPathname(pathname: string): boolean {
  return (
    pathname === "/" ||
    pathname === "/pricing" ||
    pathname === "/app" ||
    pathname.startsWith("/app/")
  );
}

/** Accept only same-app return destinations and canonicalise them fail-closed. */
export function normalizeInternalAppReturnPath(
  value: string | null,
): string | null {
  const candidate = safePath(value);
  const unsafe =
    !candidate ||
    !candidate.startsWith("/") ||
    candidate.startsWith("//") ||
    candidate.includes("\\") ||
    containsControlCharacter(candidate) ||
    /%(?:0a|0d|5c)/i.test(candidate);
  if (unsafe) return null;
  try {
    const parsed = new URL(candidate, INTERNAL_APP_ORIGIN);
    if (parsed.origin !== INTERNAL_APP_ORIGIN) return null;
    if (!isAllowedReturnPathname(parsed.pathname)) return null;
    return canonicalInternalPath(parsed);
  } catch {
    return null;
  }
}
